import type { SoundId } from './types'

type AudioCtx = AudioContext

/** Procedural original SFX — abstract, non-realistic (esp. cannon ≠ firearm). */
export function playSynth(
  ctx: AudioCtx,
  id: SoundId,
  gainNode: GainNode,
  when = 0
): { stop: () => void } {
  const t0 = ctx.currentTime + when
  const oscillators: OscillatorNode[] = []
  const gains: GainNode[] = []

  const tone = (
    type: OscillatorType,
    freq: number,
    dur: number,
    peak: number,
    opts?: { slideTo?: number; delay?: number }
  ) => {
    const osc = ctx.createOscillator()
    const g = ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(freq, t0 + (opts?.delay ?? 0))
    if (opts?.slideTo != null) {
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(20, opts.slideTo),
        t0 + (opts?.delay ?? 0) + dur
      )
    }
    g.gain.setValueAtTime(0.0001, t0 + (opts?.delay ?? 0))
    g.gain.exponentialRampToValueAtTime(peak, t0 + (opts?.delay ?? 0) + 0.01)
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + (opts?.delay ?? 0) + dur)
    osc.connect(g)
    g.connect(gainNode)
    osc.start(t0 + (opts?.delay ?? 0))
    osc.stop(t0 + (opts?.delay ?? 0) + dur + 0.02)
    oscillators.push(osc)
    gains.push(g)
  }

  switch (id) {
    case 'ui.confirm':
      tone('sine', 660, 0.08, 0.25)
      tone('sine', 880, 0.06, 0.15, { delay: 0.04 })
      break
    case 'ui.place':
      tone('triangle', 420, 0.1, 0.22)
      tone('sine', 630, 0.08, 0.12, { delay: 0.03 })
      break
    case 'ui.upgrade':
      tone('sine', 520, 0.12, 0.2, { slideTo: 780 })
      break
    case 'ui.sell':
      tone('triangle', 380, 0.12, 0.18, { slideTo: 220 })
      break
    case 'ui.waveStart':
      tone('square', 300, 0.08, 0.12)
      tone('sine', 450, 0.1, 0.16, { delay: 0.06 })
      break
    case 'tower.fire':
      tone('sawtooth', 480, 0.06, 0.14, { slideTo: 220 })
      break
    case 'tower.frost':
      tone('sine', 920, 0.05, 0.12)
      tone('triangle', 1240, 0.04, 0.08, { delay: 0.02 })
      break
    case 'tower.cannon':
      // Low synthetic pulse — deliberately not a gunshot.
      tone('sine', 90, 0.12, 0.28, { slideTo: 55 })
      tone('triangle', 140, 0.08, 0.1)
      break
    case 'tower.poison':
      tone('triangle', 260, 0.09, 0.14, { slideTo: 200 })
      tone('sine', 340, 0.07, 0.08, { delay: 0.03 })
      break
    case 'tower.aa':
      // Sharp synthetic pulse / tracking chirp — not gunfire.
      tone('square', 1180, 0.04, 0.1, { slideTo: 880 })
      tone('sine', 1560, 0.03, 0.06, { delay: 0.02 })
      break
    case 'tower.mage':
      // Soft synthetic prism hum / focused bolt.
      tone('sine', 640, 0.07, 0.12)
      tone('triangle', 980, 0.05, 0.09, { delay: 0.025 })
      break
    case 'sig.burn':
      tone('sawtooth', 360, 0.1, 0.16, { slideTo: 500 })
      break
    case 'sig.deepFreeze':
      tone('sine', 700, 0.16, 0.2)
      tone('triangle', 1050, 0.12, 0.12, { delay: 0.04 })
      break
    case 'sig.shockwave':
      tone('sine', 70, 0.18, 0.3, { slideTo: 40 })
      tone('triangle', 180, 0.1, 0.12)
      break
    case 'sig.contagion':
      tone('triangle', 310, 0.14, 0.14, { slideTo: 420 })
      break
    case 'sig.multiLock':
      tone('square', 980, 0.05, 0.1)
      tone('square', 1320, 0.04, 0.08, { delay: 0.04 })
      tone('sine', 1600, 0.04, 0.06, { delay: 0.08 })
      break
    case 'sig.armorBreak':
      // Short crystalline crack — distinct from burn/freeze.
      tone('square', 720, 0.04, 0.09, { slideTo: 420 })
      tone('sine', 1100, 0.05, 0.07, { delay: 0.02 })
      break
    case 'enemy.statusReject':
      // Soft muted ping — readable, never loud DoT spam.
      tone('triangle', 380, 0.06, 0.1, { slideTo: 260 })
      break
    case 'enemy.shieldBreak':
      tone('square', 500, 0.05, 0.1)
      tone('sawtooth', 280, 0.12, 0.16, { slideTo: 120, delay: 0.03 })
      break
    case 'enemy.bossSpawn':
      tone('sine', 110, 0.25, 0.25, { slideTo: 80 })
      tone('triangle', 160, 0.2, 0.12, { delay: 0.08 })
      break
    case 'enemy.bossDeath':
      tone('sawtooth', 200, 0.22, 0.22, { slideTo: 60 })
      tone('sine', 90, 0.28, 0.18, { delay: 0.05 })
      break
    case 'enemy.leak':
      tone('triangle', 240, 0.14, 0.18, { slideTo: 140 })
      break
    case 'match.waveClear':
      tone('sine', 520, 0.1, 0.18)
      tone('sine', 660, 0.1, 0.16, { delay: 0.08 })
      tone('sine', 780, 0.12, 0.14, { delay: 0.16 })
      break
    case 'match.victory':
      tone('sine', 440, 0.12, 0.2)
      tone('sine', 554, 0.12, 0.18, { delay: 0.1 })
      tone('sine', 659, 0.18, 0.2, { delay: 0.2 })
      break
    case 'match.defeat':
      tone('triangle', 280, 0.2, 0.2, { slideTo: 120 })
      tone('sine', 180, 0.25, 0.14, { delay: 0.08 })
      break
    default:
      tone('sine', 440, 0.06, 0.1)
  }

  return {
    stop: () => {
      for (const osc of oscillators) {
        try {
          osc.stop()
          osc.disconnect()
        } catch {
          /* already stopped */
        }
      }
      for (const g of gains) {
        try {
          g.disconnect()
        } catch {
          /* */
        }
      }
    }
  }
}
